import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import Button from '@/components/Button/Button';
import { useToast } from '@/hooks/useToast';
import { getEvaluationScales, saveEvaluationScale, deleteEvaluationScale, EvaluationScale, EvaluationScaleOption } from '@/features/PerformanceCriteria/services/scalesService';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';

export const ScalesPage: React.FC = () => {
    const toast = useToast();
    const [scales, setScales] = useState<EvaluationScale[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedScale, setSelectedScale] = useState<EvaluationScale | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [scaleContext, setScaleContext] = useState<'LEADER' | 'COLLABORATOR'>('LEADER');

    useEffect(() => {
        loadScales();
    }, []);

    const loadScales = async () => {
        setLoading(true);
        try {
            const data = await getEvaluationScales();
            setScales(data);
        } catch (error) {
            toast.error("Erro", "Falha ao carregar escalas.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateScale = () => {
        const defaultOptions: EvaluationScaleOption[] = [
            { value: 0, label: 'N/A', description: '', labelLeader: 'N/A', descriptionLeader: '', labelCollaborator: '', descriptionCollaborator: '' },
            { value: 1, label: 'Insuficiente', description: '', labelLeader: 'Insuficiente', descriptionLeader: '', labelCollaborator: '', descriptionCollaborator: '' },
            { value: 2, label: 'Regular', description: '', labelLeader: 'Regular', descriptionLeader: '', labelCollaborator: '', descriptionCollaborator: '' },
            { value: 3, label: 'Bom', description: '', labelLeader: 'Bom', descriptionLeader: '', labelCollaborator: '', descriptionCollaborator: '' },
            { value: 4, label: 'Muito Bom', description: '', labelLeader: 'Muito Bom', descriptionLeader: '', labelCollaborator: '', descriptionCollaborator: '' },
            { value: 5, label: 'Excelente', description: '', labelLeader: 'Excelente', descriptionLeader: '', labelCollaborator: '', descriptionCollaborator: '' }
        ];

        const newScale: EvaluationScale = {
            name: '',
            description: '',
            options: defaultOptions
        };
        setSelectedScale(newScale);
        setIsEditing(true);
    };

    const handleSelectScale = (scale: EvaluationScale) => {
        if (isEditing) {
            if (!confirm("Você tem alterações não salvas. Deseja descartar?")) return;
        }
        setSelectedScale(JSON.parse(JSON.stringify(scale)));
        setIsEditing(false);
    };

    const handleSaveScale = async () => {
        if (!selectedScale) return;
        if (!selectedScale.name.trim()) {
            toast.error("Erro", "Nome da escala é obrigatório.");
            return;
        }

        try {
            await saveEvaluationScale(selectedScale);
            toast.success("Sucesso", "Escala salva com sucesso.");
            setIsEditing(false);
            loadScales();
            setSelectedScale(null);
        } catch (error) {
            toast.error("Erro", "Falha ao salvar escala.");
        }
    };

    const handleDeleteScale = async (scale: EvaluationScale) => {
        if (!scale.id) {
            setSelectedScale(null);
            setIsEditing(false);
            return;
        }
        if (!confirm(`Tem certeza que deseja excluir a escala "${scale.name}"?`)) return;

        try {
            await deleteEvaluationScale(scale.id);
            toast.success("Sucesso", "Escala excluída.");
            if (selectedScale?.id === scale.id) {
                setSelectedScale(null);
                setIsEditing(false);
            }
            loadScales();
        } catch (error) {
            toast.error("Erro", "Falha ao excluir escala.");
        }
    };

    const updateScaleOption = (index: number, field: keyof EvaluationScaleOption, value: any) => {
        if (!selectedScale) return;
        const newOptions = [...selectedScale.options];
        const option = { ...newOptions[index], [field]: value };

        // Sync legacy fields
        if (field === 'label' || field === 'labelLeader') {
            option.label = value;
            option.labelLeader = value;
        }
        if (field === 'description' || field === 'descriptionLeader') {
            option.description = value;
            option.descriptionLeader = value;
        }

        newOptions[index] = option;
        setSelectedScale({ ...selectedScale, options: newOptions });
        setIsEditing(true);
    };

    return (
        <div className="space-y-6">
            <SubPageHeader
                backTo="/people"
                icon={<AdjustmentsHorizontalIcon className="h-6 w-6" aria-hidden />}
                badge="Avaliações"
                title="Escalas"
                description="Defina as opções de pontuação para as avaliações."
            />

            <div className="h-[calc(100vh-200px)] overflow-hidden bg-gray-50 rounded-lg border border-gray-200">
                <div className="h-full flex flex-col md:flex-row gap-6 p-6 overflow-hidden">
                    {/* Sidebar: Scale List */}
                    <div className="w-full md:w-1/3 bg-white shadow rounded-lg flex flex-col h-full overflow-hidden">
                        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50 flex-shrink-0">
                            <h2 className="text-lg font-medium text-gray-900">Escalas Cadastradas</h2>
                            <Button size="sm" onClick={handleCreateScale}>
                                <PlusIcon className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 space-y-2">
                            {loading ? (
                                <p className="p-4 text-gray-500 text-sm">Carregando...</p>
                            ) : (
                                scales.map(scale => (
                                    <div
                                        key={scale.id || 'new'}
                                        onClick={() => handleSelectScale(scale)}
                                        className={`cursor-pointer p-3 rounded-md flex justify-between items-center group transition-colors ${selectedScale?.id === scale.id ? 'bg-indigo-50 border-indigo-200 border text-indigo-700' : 'hover:bg-gray-50 border border-transparent'
                                            }`}
                                    >
                                        <span className="font-medium truncate">{scale.name || '(Nova Escala)'}</span>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteScale(scale); }}
                                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-opacity"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                            {scales.length === 0 && !loading && (
                                <p className="text-center text-gray-500 text-sm py-8">Nenhuma escala cadastrada.</p>
                            )}
                        </div>
                    </div>

                    {/* Main Area: Scale Editor */}
                    <div className="w-full md:w-2/3 bg-white shadow rounded-lg flex flex-col h-full overflow-hidden">
                        {selectedScale ? (
                            <>
                                <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-col gap-4 flex-shrink-0">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1 mr-4 space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Nome da Escala</label>
                                                <input
                                                    type="text"
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-semibold"
                                                    value={selectedScale.name}
                                                    onChange={(e) => {
                                                        setSelectedScale({ ...selectedScale, name: e.target.value });
                                                        setIsEditing(true);
                                                    }}
                                                    placeholder="Ex: Escala Padrão 1-5"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Descrição/Objetivo</label>
                                                <input
                                                    type="text"
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                                    value={selectedScale.description}
                                                    onChange={(e) => {
                                                        setSelectedScale({ ...selectedScale, description: e.target.value });
                                                        setIsEditing(true);
                                                    }}
                                                    placeholder="Utilizada para avaliação de competências técnicas..."
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 mt-6">
                                            {isEditing && (
                                                <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded">
                                                    Não salvo
                                                </span>
                                            )}
                                            <Button onClick={handleSaveScale} disabled={!isEditing}>
                                                Salvar Escala
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-6 bg-white">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-medium text-gray-900">Opções da Escala</h3>

                                        <div className="bg-gray-100 p-1 rounded-lg flex text-xs font-medium">
                                            <button
                                                onClick={() => setScaleContext('LEADER')}
                                                className={`px-3 py-1.5 rounded-md transition-all ${scaleContext === 'LEADER' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                Visão do Líder
                                            </button>
                                            <button
                                                onClick={() => setScaleContext('COLLABORATOR')}
                                                className={`px-3 py-1.5 rounded-md transition-all ${scaleContext === 'COLLABORATOR' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                Visão do Colaborador
                                            </button>
                                        </div>
                                    </div>

                                    {scaleContext === 'COLLABORATOR' && (
                                        <div className="mb-4 p-3 bg-blue-50 text-blue-700 text-sm rounded-md border border-blue-100">
                                            Defina como as opções aparecerão para o colaborador durante a <strong>Autoavaliação</strong>.
                                            Se deixado em branco, será usado o texto da Visão do Líder.
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        {selectedScale.options.sort((a, b) => a.value - b.value).map((option, index) => (
                                            <div key={option.value} className="flex gap-4 p-4 border border-gray-100 rounded-lg bg-gray-50">
                                                <div className="w-12 flex-shrink-0 flex flex-col items-center justify-center bg-gray-200 rounded-md">
                                                    <span className="text-lg font-bold text-gray-700">{option.value}</span>
                                                </div>
                                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {scaleContext === 'LEADER' ? (
                                                        <>
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500">Rótulo (Líder)</label>
                                                                <input
                                                                    type="text"
                                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                                    value={option.labelLeader}
                                                                    onChange={(e) => updateScaleOption(index, 'labelLeader', e.target.value)}
                                                                    placeholder="Rótulo"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500">Descrição (Líder)</label>
                                                                <input
                                                                    type="text"
                                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                                    value={option.descriptionLeader}
                                                                    onChange={(e) => updateScaleOption(index, 'descriptionLeader', e.target.value)}
                                                                    placeholder="Descrição detalhada"
                                                                />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500">Rótulo (Colaborador)</label>
                                                                <input
                                                                    type="text"
                                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                                    value={option.labelCollaborator || ''}
                                                                    onChange={(e) => updateScaleOption(index, 'labelCollaborator', e.target.value)}
                                                                    placeholder={option.labelLeader}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-500">Descrição (Colaborador)</label>
                                                                <input
                                                                    type="text"
                                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                                    value={option.descriptionCollaborator || ''}
                                                                    onChange={(e) => updateScaleOption(index, 'descriptionCollaborator', e.target.value)}
                                                                    placeholder={option.descriptionLeader}
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                    <PlusIcon className="h-8 w-8 text-gray-300" />
                                </div>
                                <p className="text-lg font-medium text-gray-500">Selecione ou crie uma Escala</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
